import { EMPTY, formatDateFull, formatDateNear, formatDays, formatInteger, isNumber } from './piFormat.js';
import { featureRoute } from './piNavigation.js';
import { MODULE_OPTIONS } from './piQuery.js';
import { listOf, objectOf } from './piRequests.js';
import { moduleName } from './piFeaturesModel.js';
import { COMMERCIAL_STATES, commercialClassText, commercialStateLabel, commercialStateShort, opportunityLink } from './piOpportunitiesModel.js';
import { defineColumns } from './piColumns.js';

export const COVERAGE_STATES = ['full', 'partial', 'none'];

const NOT_RELEASED = 'nao_liberada';

const textOf = (value) => (value === null || value === undefined ? '' : String(value).trim());

export const coverageOf = (value) => {
  const key = textOf(value).toLowerCase();
  return COVERAGE_STATES.indexOf(key) !== -1 ? key : '';
};

const moduleOrder = (module) => {
  const raw = textOf(module);
  if (/^\d+$/.test(raw)) return Number(raw);
  const known = MODULE_OPTIONS.filter((option) => option.label === raw)[0];
  return known ? Number(known.value) : 1000;
};

export const sellableText = (sellable) => {
  if (sellable === true) return 'Vendável';
  if (sellable === false) return 'Não vendável';
  return EMPTY;
};

export const measuredText = (measured, coverage, measuredFrom, now) => {
  if (measured === false) return 'Sem medição';
  if (measured !== true) return EMPTY;
  const since = formatDateNear(measuredFrom, now);
  const kind = coverageOf(coverage);
  if (kind === 'none') return since !== EMPTY ? 'Medição começou em ' + since + ', depois da janela' : 'Medição começou depois da janela';
  if (kind === 'partial') return since !== EMPTY ? 'Medição desde ' + since + ' (janela incompleta)' : 'Medição em parte da janela';
  return since !== EMPTY ? 'Medida desde ' + since : 'Medida';
};

const adoptingOf = (usage) => {
  const source = objectOf(usage);
  return isNumber(source.adoptingUsers) ? source.adoptingUsers : null;
};

const notReleased = (context) => textOf(objectOf(context).stateKey).toLowerCase() === NOT_RELEASED;

const usageHidden = (usage, context) => {
  if (coverageOf(objectOf(context).coverage) === 'none') return true;
  const adopting = adoptingOf(usage);
  return notReleased(context) && !(adopting > 0);
};

export const usageText = (usage, measured, context) => {
  if (measured === false) return 'Sem medição';
  if (usageHidden(usage, context)) return EMPTY;
  const source = objectOf(usage);
  const adopting = adoptingOf(usage);
  const eligible = isNumber(source.eligibleUsers) ? source.eligibleUsers : null;
  if (adopting === null && eligible === null) return EMPTY;
  if (eligible === null || notReleased(context)) return formatInteger(adopting) + (adopting === 1 ? ' usuário usou' : ' usuários usaram');
  return formatInteger(adopting === null ? 0 : adopting) + ' de ' + formatInteger(eligible) + (eligible === 1 ? ' usuário elegível' : ' usuários elegíveis');
};

export const usageDetailText = (usage, measured, context) => {
  if (measured === false) return '';
  if (usageHidden(usage, context)) return '';
  const source = objectOf(usage);
  const options = objectOf(context);
  const partial = coverageOf(options.coverage) === 'partial';
  const parts = [];
  if (isNumber(source.activeDays)) parts.push(formatDays(source.activeDays) + ' com uso');
  const last = formatDateFull(source.lastUsedOn);
  if (last !== EMPTY) parts.push('último uso em ' + last);
  else if (!partial) parts.push('sem uso registrado');
  if (partial) {
    const since = formatDateNear(options.measuredFrom, options.now);
    parts.push(since !== EMPTY ? 'medido só desde ' + since : 'medido só em parte da janela');
  }
  return parts.join(' · ');
};

export const portfolioRow = (feature, company, now) => {
  const source = objectOf(feature);
  const state = objectOf(source.commercialState);
  const owner = objectOf(company);
  const opportunityId = textOf(source.opportunityId);
  const name = textOf(source.name) || (isNumber(source.featureType) ? 'Funcionalidade ' + source.featureType : 'Funcionalidade');
  const stateKey = textOf(state.state).toLowerCase();
  const coverage = coverageOf(source.coverage);
  const measuredFrom = textOf(source.measuredFrom) || null;
  const context = { coverage: coverage, measuredFrom: measuredFrom, stateKey: stateKey, now: now };
  return {
    key: textOf(source.featureType) || name,
    featureType: isNumber(source.featureType) ? source.featureType : null,
    name: name,
    route: featureRoute(source.featureType),
    module: source.module,
    classText: commercialClassText(source.commercialClass),
    stateKey: stateKey,
    stateText: textOf(state.label) || commercialStateLabel(state.state),
    sellableText: sellableText(source.sellable),
    coverage: coverage,
    measuredFrom: measuredFrom,
    measuredText: measuredText(source.measured, coverage, measuredFrom, now),
    usageText: usageText(source.usage, source.measured, context),
    usageDetailText: usageDetailText(source.usage, source.measured, context),
    opportunityId: opportunityId,
    opportunityLink: opportunityId ? opportunityLink(owner.companyId, owner.name, source.featureType) : null,
  };
};

const stateRank = (key) => {
  for (let i = 0; i < COMMERCIAL_STATES.length; i++) {
    if (COMMERCIAL_STATES[i].key === key) return i;
  }
  return COMMERCIAL_STATES.length;
};

export const portfolioView = (data, fallbackCompany, now) => {
  const source = objectOf(data);
  const fallback = objectOf(fallbackCompany);
  const given = objectOf(source.company);
  const company = {
    companyId: textOf(given.companyId) || textOf(fallback.companyId),
    name: textOf(given.name) || textOf(fallback.name),
  };
  const rows = listOf(source.features).map((feature) => portfolioRow(feature, company, now));
  const modules = [];
  rows.forEach((row) => {
    const key = textOf(row.module) || 'none';
    let group = modules.filter((item) => item.key === key)[0];
    if (!group) {
      group = { key: key, label: row.module === null || row.module === undefined || row.module === '' ? 'Sem módulo' : moduleName(row.module), order: moduleOrder(row.module), rows: [] };
      modules.push(group);
    }
    group.rows.push(row);
  });
  modules.sort((a, b) => (a.order !== b.order ? a.order - b.order : a.label.localeCompare(b.label, 'pt-BR')));
  modules.forEach((group) => group.rows.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')));
  const counts = {};
  rows.forEach((row) => {
    counts[row.stateKey] = (counts[row.stateKey] || 0) + 1;
  });
  return {
    companyName: company.name,
    modules: modules,
    total: rows.length,
    opportunities: rows.filter((row) => row.opportunityId).length,
    summary: Object.keys(counts).sort((a, b) => stateRank(a) - stateRank(b) || a.localeCompare(b, 'pt-BR')).map((key) => {
      const label = commercialStateShort(key);
      return { key: key, label: label, count: counts[key], text: label + ': ' + formatInteger(counts[key]) };
    }),
  };
};

const plainText = (value) => {
  const text = textOf(value);
  return text === EMPTY ? '' : text;
};

const rowOf = (row) => objectOf(row);

export const PORTFOLIO_COLUMNS = defineColumns([
  { key: 'moduleLabel', label: 'Módulo', type: 'text', infoKey: 'revenue.portfolio.module', screen: false, width: 28 },
  { key: 'name', label: 'Funcionalidade', type: 'text', infoKey: 'revenue.portfolio.feature', width: 36 },
  { key: 'classText', label: 'Classe comercial', type: 'text', infoKey: 'revenue.commercialClass', screen: false },
  { key: 'stateText', label: 'Estado comercial', type: 'text', infoKey: 'revenue.commercialState', width: 40 },
  { key: 'sellableText', label: 'Venda', type: 'text', infoKey: 'revenue.portfolio.sellable', value: (row) => plainText(rowOf(row).sellableText) },
  { key: 'measuredText', label: 'Medição', type: 'text', infoKey: 'revenue.portfolio.measured', value: (row) => plainText(rowOf(row).measuredText) },
  { key: 'usageText', label: 'Uso', type: 'text', infoKey: 'revenue.portfolio.usage', value: (row) => plainText(rowOf(row).usageText) },
  { key: 'usageDetailText', label: 'Detalhe do uso', type: 'text', infoKey: 'revenue.portfolio.usageDetail', screen: false },
  {
    key: 'opportunity', label: 'Oportunidade', type: 'text', infoKey: 'revenue.portfolio.opportunity',
    value: (row) => (rowOf(row).opportunityId ? 'Aberta (nº ' + rowOf(row).opportunityId + ')' : ''),
  },
]);

export const portfolioExportRows = (view) => {
  const rows = [];
  listOf(objectOf(view).modules).forEach((group) => {
    listOf(group.rows).forEach((row) => rows.push(Object.assign({ moduleLabel: group.label }, row)));
  });
  return rows;
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piPortfolioModel.js